/**
 * The Fields panel — list engine v2.
 *
 * A search box, the shown columns in display order with a grip, a pin and a
 * toggle, then the hidden ones below.
 *
 * The one rule worth stating: pinned fields sort to the top and cannot be
 * dragged below an unpinned one. A pinned column is pinned *to the left edge*
 * of the table, so a pinned field sitting below an unpinned field in this list
 * would be describing an order the table cannot draw.
 */
import { useState } from 'react'
import { Checkbox, Switch } from './primitives.js'
import {
  ControlPanel, PANEL_ROW, PanelSection, panelFacts, usePanelSearch,
} from './control-panel.js'
import { COLUMN_ICONS, IconGrip, IconPin } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import type { ListFieldDef, UseListResult } from '../lib/use-list.js'

export function FieldsPanel({
  list, onBack, bare,
}: {
  list: UseListResult
  onBack?: () => void
  /** In a sheet: the rows and the search well, with no shell of its own. */
  bare?: boolean
}) {
  const facts = panelFacts('fields', list)
  const { query, setQuery, matches } = usePanelSearch()
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)

  const pinned = list.view.pinned ?? []
  const shownNames = list.visibleFields.map((field) => field.name)

  /*
   * Columns, not every field. The catalogue also carries the ones that are
   * only ever filtered on — a relation is an id, worth picking a value from
   * and never worth printing — and offering those here would be offering a
   * uuid as a column.
   */
  const columns = list.fields.filter((field) => field.selectable !== false)

  const shown = list.visibleFields.filter(matches)
  const hidden = columns
    .filter((field) => !shownNames.includes(field.name))
    .filter(matches)

  const drop = (onto: string) => {
    if (!dragging || dragging === onto) return
    const order = list.visibleFields.map((field) => field.name)
    list.moveField(dragging, order.indexOf(onto))
    setDragging(null)
    setOver(null)
  }

  return (
    <ControlPanel
      bare={bare}
      title="Fields"
      count={facts.count}
      search={{ value: query, onChange: setQuery, placeholder: 'Find a field' }}
      onBack={onBack}
      {...(facts.reset
        ? { onReset: facts.reset.run, resetLabel: facts.reset.label }
        : {})}
    >
      <PanelSection label="Shown · drag to reorder" first>
        {shown.map((field) => (
          <FieldRow
            key={field.name}
            field={field}
            shown
            pinned={pinned.includes(field.name)}
            isOver={over === field.name && dragging !== field.name}
            onDragStart={() => setDragging(field.name)}
            onDragEnd={() => { setDragging(null); setOver(null) }}
            onDragOver={() => setOver(field.name)}
            onDrop={() => drop(field.name)}
            onTogglePin={() => list.togglePin(field.name)}
            onToggle={() => list.toggleField(field.name)}
          />
        ))}
        {shown.length === 0 && (
          <li className="py-1 text-cell text-text-muted">No columns match.</li>
        )}
      </PanelSection>

      {hidden.length > 0 && (
        <PanelSection label="Hidden">
          {hidden.map((field) => (
            <FieldRow
              key={field.name}
              field={field}
              shown={false}
              pinned={false}
              isOver={false}
              onToggle={() => list.toggleField(field.name)}
            />
          ))}
        </PanelSection>
      )}
    </ControlPanel>
  )
}

function FieldRow({
  field, shown, pinned, isOver, onDragStart, onDragEnd, onDragOver, onDrop, onTogglePin, onToggle,
}: {
  field: ListFieldDef
  shown: boolean
  pinned: boolean
  isOver: boolean
  onDragStart?: () => void
  onDragEnd?: () => void
  onDragOver?: () => void
  onDrop?: () => void
  onTogglePin?: () => void
  onToggle: () => void
}) {
  const Icon = COLUMN_ICONS[field.icon]

  return (
    <li
      draggable={shown}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(event) => { event.preventDefault(); onDragOver?.() }}
      onDrop={(event) => { event.preventDefault(); onDrop?.() }}
      style={{ minHeight: PANEL_ROW }}
      className={cn(
        'flex items-center gap-1.5 rounded-field px-1',
        shown && 'cursor-grab active:cursor-grabbing',
        // The green insertion line the spec asks for, drawn on the row the
        // pointer is over rather than between rows — one element, no gaps to
        // fall through.
        isOver && 'shadow-[inset_0_2px_0_0_rgb(var(--color-accent))]',
      )}
    >
      {shown
        ? <span className="shrink-0 text-text-faint"><IconGrip size={14} /></span>
        : <span aria-hidden className="w-[14px] shrink-0" />}

      <span className="shrink-0 text-text-muted"><Icon size={14} /></span>
      <span className="min-w-0 flex-1 truncate text-cell text-text">{field.label}</span>

      {shown && onTogglePin && (
        <button
          type="button"
          aria-label={pinned ? `Unpin ${field.label}` : `Pin ${field.label} left`}
          aria-pressed={pinned}
          onClick={onTogglePin}
          className={cn(
            'shrink-0 rounded-field p-0.5',
            pinned ? 'text-accent' : 'text-text-faint hover:text-text-muted',
          )}
        >
          <IconPin size={13} />
        </button>
      )}

      <Switch checked={shown} onChange={onToggle} label={`Show ${field.label}`} hideLabel />
    </li>
  )
}

/** Kept for the summary menus that still want a plain tick list. */
export function FieldTicks({ list }: { list: UseListResult }) {
  return (
    <ul className="flex list-none flex-col gap-0.5 p-0">
      {list.fields.filter((field) => field.selectable !== false).map((field) => (
        <li key={field.name}>
          <Checkbox
            checked={list.visibleFields.some((shown) => shown.name === field.name)}
            onChange={() => list.toggleField(field.name)}
            label={field.label}
          />
        </li>
      ))}
    </ul>
  )
}
